import { useEffect } from "react";
import { createPortal } from "react-dom";
import { setDefaultLocale } from "react-datepicker";
import { I18nextProvider } from "react-i18next";
import { Route, Routes, Navigate } from "react-router-dom";
// import NavBar from "./components/navigation/NavBar/NavBar";
// import PublicNavBar from "./components/navigation/PublicNavBar/PublicNavBar";
// import StudentNavBar from "./components/navigation/StudentNavBar/StudentNavBar";
import ProtectedRoute from "./components/routing/ProtectedRoute/ProtectedRoute";
import i18n from "./lang/i18n";
import "./lang/syncLanguage";

import NotFound from "./pages/NotFound/NotFound";
import { UserContext } from "./utils/contexts/UserContext";
import useFindUser from "./utils/hooks/useFindUser";
import useSessionExpired from "./utils/hooks/useSessionExpired";
import CreateQuestion from "./pages/MyQuestions/CreateQuestion/CreateQuestion";
import Login from "../src/pages/Auth/Login/Login";
import Questions from "../src/pages/Questions/Questions";
import Passages from "./pages/Passages/Passages";
import Folders from "./pages/Folders/Folders";
import MyTasks from "./pages/MyTasks/MyTasks";
import FolderLink from "./pages/Questions/FolderLink";
import Exams1 from "../src/pages/Exam/Exams";
import ViewExam from '../src/pages/Exam/ExamManagement';
import Exams from '../src/pages/Exam';
import ExamsList from '../src/pages/Exams/ExamsList';
import ViewExams from '../src/pages/Exams/ViewExams';
import CreateTest from '../src/pages/Exams/CreateTest';
import CreateSingleTest from '../src/pages/Exams/CreateSingleTest';
import CreateExam from '../src/pages/Exams/CreateExam';
import TestBuilder from '../src/pages/Exams/TestBuilder/TestBuilder';
import TestList from '../src/pages/Exams/TestList';
import MyQuestions from "../src/pages/MyQuestions/MyQuestions";
import BaseLayout from "./components/layout/BaseLayout/BaseLayout";
import PublicLayout from "./components/layout/PublicLayout/PublicLayout";
import EditQuestion from "./pages/MyQuestions/EditQuetion/EditQuestion";
import QuestionPage from "./pages/QuestionPage/Main";
import Evaluators from "./pages/Evaluators/Evaluators";
import MyEvaluations from "./pages/Evaluators/MyEvaluations";
import { ToastContainer } from "react-toastify";
import EditDraftQuestion from "./pages/MyQuestions/EditQuetion/EditDraftQuestion";
import ExamLayout from "./components/layout/ExamLayout/ExamLayout";
import AvailableExams from "./pages/ExamTaking/AvailableExams";
import ExamDetail from "./pages/ExamTaking/ExamDetail";
import ExamSession from "./pages/ExamTaking/ExamSession";
import ExamResult from "./pages/ExamTaking/ExamResult";
import ExamReview from "./pages/ExamTaking/ExamReview";
import MyAttempts from "./pages/ExamTaking/MyAttempts";
import BtsList from "./pages/Bts/BtsList";
import BtsCreate from "./pages/Bts/BtsCreate";
import BtsDetail from "./pages/Bts/BtsDetail";
import ExternalLaunch from "./pages/ExternalLaunch/ExternalLaunch";
import ExternalExamSession from "./pages/ExternalLaunch/ExternalExamSession";
import ExternalExamResult from "./pages/ExternalLaunch/ExternalExamResult";
import { canOpenPage, getHomePage } from "./utils/permissions";


function SessionExpiredHandler() {
  useSessionExpired();
  return null;
}

function App() {
  const { user, setUser, isLoading } = useFindUser();

  useEffect(() => {
    setDefaultLocale(localStorage.getItem("i18nextLng"));
    return () => {};
  }, []);

  return (
    <I18nextProvider i18n={i18n}>
      <UserContext.Provider value={{ user, setUser, isLoading }}>
        <SessionExpiredHandler />
        {/* Outside #root: an open dialog makes #root inert, and toast actions (e.g. Undo) must stay clickable. */}
        {createPortal(<ToastContainer position="bottom-right" />, document.body)}

        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/login" element={<Login />} />
          </Route>
          <Route
            path="/"
            element={
              !user?.uuid && !isLoading ? (
                <Navigate to="/login" />
              ) : user?.uuid && user?.perms ? (
                <Navigate to={getHomePage(user)} />
              ) : (
                <BaseLayout />
              )
            }
          />
          
          <Route path="/" element={<BaseLayout />}>
            
              <Route path="exams" element={<Exams1 />} />
              <Route path="exams/:uuid" element={<ViewExam />} />

              {/* The exam bank and the test bank: create_exam (as the sidebar), so a typed URL does not show
                  buttons the server only refuses. */}
              <Route
                element={
                  <ProtectedRoute
                    isAllowed={canOpenPage(user, "/exams_list")}
                    isLoading={isLoading}
                    redirectPath={user?.uuid ? getHomePage(user) : "/login"}
                  />
                }
              >
                <Route path="exams_list" element={<ExamsList />} />
                <Route path="exams_create" element={<CreateExam />} />
                <Route path="exams_list/:uuid" element={<ViewExams />} />
                <Route path="exams_list/:uuid/create_test" element={<CreateTest />} />
              </Route>
              <Route
                element={
                  <ProtectedRoute
                    isAllowed={canOpenPage(user, "/test_list")}
                    isLoading={isLoading}
                    redirectPath={user?.uuid ? getHomePage(user) : "/login"}
                  />
                }
              >
                <Route path="test_list" element={<TestList />} />
                <Route path="test/:uuid" element={<TestBuilder />} />
                <Route path="test_create" element={<CreateSingleTest />} />
              </Route>
              {/* BTS booklets: admins only. */}
              <Route
                element={
                  <ProtectedRoute
                    isAllowed={canOpenPage(user, "/bts")}
                    isLoading={isLoading}
                    redirectPath={user?.uuid ? getHomePage(user) : "/login"}
                  />
                }
              >
                <Route path="bts" element={<BtsList />} />
                <Route path="bts/new" element={<BtsCreate />} />
                <Route path="bts/:uuid" element={<BtsDetail />} />
              </Route>

              {/* A folder link ("Share"), with or without an invite: outside the bank's guard, since joining by the
                  invite is what gives access to the bank. */}
              <Route path="questions/folders/:folderUuid" element={<FolderLink />} />
              <Route
                element={
                  <ProtectedRoute
                    isAllowed={canOpenPage(user, "/questions")}
                    isLoading={isLoading}
                    redirectPath={user?.uuid ? getHomePage(user) : "/login"}
                  />
                }
              >
                <Route path="questions" element={<Questions />} />
                {/* The bank's Texts tab: a static path, so it wins over questions/:uuid. */}
                <Route path="questions/texts" element={<Passages />} />
                <Route path="questions/:uuid" element={<QuestionPage />} />
                {/* The folders as a Finder window: who opens the bank opens them. */}
                <Route path="folders" element={<Folders />} />
                <Route path="folders/:uuid" element={<Folders />} />
              </Route>
              <Route path="my-questions" element={<MyQuestions />} />
              {/* The tasks given to the user in subjects (TZ 2.2): authors, experts and moderators. */}
              <Route
                element={
                  <ProtectedRoute
                    isAllowed={canOpenPage(user, "/my-tasks")}
                    isLoading={isLoading}
                    redirectPath={user?.uuid ? getHomePage(user) : "/login"}
                  />
                }
              >
                <Route path="my-tasks" element={<MyTasks />} />
              </Route>
              {/* The Texts tab of My Questions: the user's own passages. */}
              <Route path="my-questions/texts" element={<Passages mine />} />
              <Route path="my-questions/create" element={<CreateQuestion />} />
              {/* The experts' page: approve_question (as the sidebar); a typed URL would only meet 403s. */}
              <Route
                element={
                  <ProtectedRoute
                    isAllowed={canOpenPage(user, "/evaluators")}
                    isLoading={isLoading}
                    redirectPath={user?.uuid ? getHomePage(user) : "/login"}
                  />
                }
              >
                <Route path="evaluators" element={<Evaluators />} />
              </Route>
              <Route
                element={
                  <ProtectedRoute
                    isAllowed={canOpenPage(user, "/my-evaluations")}
                    isLoading={isLoading}
                    redirectPath={user?.uuid ? getHomePage(user) : "/login"}
                  />
                }
              >
                <Route path="my-evaluations" element={<MyEvaluations />} />
              </Route>
              <Route path="available-exams" element={<AvailableExams />} />
              <Route path="exam/:uuid/take" element={<ExamDetail />} />
              <Route path="my-attempts" element={<MyAttempts />} />
              <Route path="exam/attempt/:uuid/result" element={<ExamResult />} />
              <Route path="exam/attempt/:uuid/review" element={<ExamReview />} />
              <Route
                path="my-questions/edit/:uuid"
                element={<EditQuestion />}
              />
              <Route
                path="my-questions/draft/:uuid"
                element={<EditDraftQuestion />}
              />
              <Route path="*" element={<Navigate to="/" />} />
          </Route>
          {/* <Route index element={<Landing />} /> */}
          {/* 
          <Route index element={<Login />} /> */}
          {/* <Route>
            <Route index element={<Login />} />
            <Route path="/login" element={<Login />} />
            {/* <Route path="/confirm/:token/" element={<Confirm />} />
            <Route path="/password-reset/" element={<PasswordReset />} />
            <Route
              path="/password-reset/:token"
              element={<PasswordResetConfirm />}
            /> */}
          {/* </Route> */}

          {/* <Route
            path="/login"
            element={
              <div className="flex flex-col min-h-screen bg-gray-100">
                <Navbar />
                <main className="flex-1 p-4 bg-gray-50">
                  <Login />
                </main>
              </div>
            }
          /> */}
          {/* Updated Private Layout */}

          {/* <Route
            path="/"
            element={
              user ? (
                <div className="flex flex-col min-h-screen bg-gray-100">
                  <Navbar />
                  <div className="flex flex-1">
                    <Sidebar />
                    <main className="flex-1 p-4 bg-gray-50">
                      <Routes>
                        <Route path="/questions" element={<Questions />} />
                        <Route path="/my-questions" element={<MyQuestions />} />
                        <Route path="*" element={<Navigate to="/questions" />} />
                      </Routes>
                    </main>
                  </div>
                </div>
              ) : (
                <Navigate to="/login" />
              )
            }
          /> */}
          {/* Private part */}
          {/* Admin side */}
          {/* <Route
              element={
                <ProtectedRoute
                  // redirectPath="/applicants"
                  isAllowed={
                    !!user?.uuid &&
                    user?.roles?.some((role) => role?.code === "ADMIN")
                  }
                  isLoading={isLoading}
                />
              }
            >
              <Route
                path="/"
                element={<NavBar user={user} setUser={setUser} />}
              >
                <Route
                  path="admin/stats/results"
                  element={<ResultsStatistics />}
                />
                <Route
                  path="admin/stats/orders"
                  element={<OrderStatistics />}
                />
                <Route
                  path="admin/stats/accounts"
                  element={<AccountStatistics />}
                />
                <Route
                  path="admin/exams/:examUUID/questions"
                  element={<QuestionsAdmin />}
                />
                <Route path="admin/exams" element={<ExaminationsAdmin />} />
              </Route>
            </Route> */}
          {/* Student side */}
          {/* <Route
              element={
                <ProtectedRoute
                  isAllowed={
                    !!user?.uuid &&
                    user?.roles?.some((role) => role?.code === "STD")
                  }
                  isLoading={isLoading}
                />
              }
            >
              <Route
                path="/student"
                element={<StudentNavBar user={user} setUser={setUser} />}
              >
                <Route path="profile" element={<StudentProfile />} />
                <Route
                  path="subscriptions"
                  element={<SubscriptionPlansStudent />}
                />
                <Route
                  path="exam/:examUUID/result"
                  element={<ExaminationStudentResult />}
                />
                <Route
                  path="exam/:examUUID"
                  element={<ExaminationStudentForm />}
                />
                <Route path="order/:uuid" element={<StudentOrder />} />
                <Route path="exams" element={<ExaminationsStudent />} />
                <Route path="settings" element={<StudentSettings />} />
              </Route>
            </Route> */}
          <Route element={<ExamLayout />}>
            <Route path="/exam/attempt/:uuid" element={<ExamSession />} />
            <Route path="/launch/:sessionUuid/exam" element={<ExternalExamSession />} />
          </Route>
          <Route path="/launch/:sessionUuid/result" element={<ExternalExamResult />} />
          <Route path="/launch/:sessionUuid" element={<ExternalLaunch />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </UserContext.Provider>
    </I18nextProvider>
  );
}

export default App;